import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MenuManagement from './components/MenuManagement';
import TableManagement from './components/TableManagement';
import OrderManagement from './components/OrderManagement';
import CategoryManagement from './components/CategoryManagement';
import CustomerManagement from './components/CustomerManagement';
import DiscountManagement from './components/DiscountManagement';
import InventoryManagement from './components/InventoryManagement';
import RecipesManagement from './components/RecipesManagement';
import SuppliersManagement from './components/SuppliersManagement';
import WastedItemsManagement from './components/WastedItemsManagement';
import ReportsManagement from './components/ReportsManagement';
import ProfileManagement from './components/ProfileManagement';
import Settings from './components/Settings';
import SettingsShop from './components/settings/SettingsShop';
import SettingsAccounts from './components/settings/SettingsAccounts';
import Layout from './components/Layout';

// Protected route for admin-only pages
const AdminRoute = ({ children }) => {
  const userRole = localStorage.getItem('userRole') || 'admin';
  if (userRole === 'sales') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('adminId');
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to="/dashboard" replace /> : 
                <Login onLogin={handleLogin} />
            } 
          />
          
          <Route
            path="/"
            element={
              isAuthenticated ? 
                <Layout onLogout={handleLogout} /> : 
                <Navigate to="/login" replace />
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="menu" element={<MenuManagement />} />
            <Route path="categories" element={<CategoryManagement />} />
            <Route path="tables" element={<TableManagement />} />
            <Route path="orders" element={<OrderManagement />} />
            <Route path="customers" element={<CustomerManagement />} />
            <Route path="discounts" element={<DiscountManagement />} />
            <Route path="inventory" element={<InventoryManagement />} />
            <Route path="inventory/supplies" element={<InventoryManagement defaultTab="supplies" />} />
            <Route path="inventory/ingredients" element={<InventoryManagement defaultTab="ingredients" />} />
            <Route path="inventory/expenses" element={<InventoryManagement defaultTab="imports" />} />
            <Route path="inventory/suppliers" element={<SuppliersManagement />} />
            <Route path="inventory/recipes" element={<RecipesManagement />} />
            <Route path="inventory/wasted" element={<WastedItemsManagement />} />
            <Route path="reports" element={<AdminRoute><ReportsManagement /></AdminRoute>} />
            <Route path="profile" element={<ProfileManagement />} />
            <Route path="settings" element={<AdminRoute><Navigate to="/settings/general" replace /></AdminRoute>} />
            <Route path="settings/shop" element={<AdminRoute><SettingsShop /></AdminRoute>} />
            <Route path="settings/accounts" element={<AdminRoute><SettingsAccounts /></AdminRoute>} />
            <Route path="settings/points" element={<AdminRoute><Settings defaultTab="points" /></AdminRoute>} />
            <Route path="settings/print" element={<AdminRoute><Settings defaultTab="print" /></AdminRoute>} />
            <Route path="settings/orders" element={<AdminRoute><Settings defaultTab="orders" /></AdminRoute>} />
            <Route path="settings/general" element={<AdminRoute><Settings defaultTab="general" /></AdminRoute>} />
          </Route>

          {/* Only redirect to login if not authenticated, otherwise to dashboard */}
          <Route 
            path="*" 
            element={
              isAuthenticated ? 
                <Navigate to="/dashboard" replace /> : 
                <Navigate to="/login" replace />
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
